import { useFormikContext } from 'formik'
import React, { useEffect, useMemo, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'

import { useNavigate } from '@lib/react-router-dom'

import { useSessionLanguage } from '@context/SessionSettingsContext'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import type { MasterTransaction } from '@model/MasterTransaction'
import {
  isPaymentProviderBankType,
  isPaymentProviderCardProviderType,
} from '@model/PaymentProviderType'
import {
  TransactionStatus,
  isTransactionStatusAwaitingApproval,
  isTransactionStatusPending,
  isTransactionStatusUnSuccessful,
} from '@model/TransactionStatus'

import { getDepositBasePath } from '../DepositFlowLayout/helpers'
import type { DepositFormValues } from '../DepositFlowLayout/types'
import { DepositTarget } from '../hooks/types'
import { DepositFailedContent } from './DepositFailedContent'
import { DepositPendingContent } from './DepositPendingContent'
import { DepositSuccessContent } from './DepositSuccessContent'

type Status = 'succeed' | 'failed' | 'completed'

type SuccessLocationState = {
  data: MasterTransaction
  providerCategoryId: number
  navigateToTransactionHistory?: boolean
}

type RedirectStorageState = {
  id: string
  data: MasterTransaction
  providerCategoryId: number
  match?: { walletId?: string; tradingAccountId?: string }
  redirectPath?: string
}

const isSuccessLocationState = (v: any): v is SuccessLocationState =>
  Boolean(v?.data && typeof v?.providerCategoryId === 'number')

const isRedirectStorageState = (v: any): v is RedirectStorageState =>
  Boolean(v?.id && v?.data && typeof v?.providerCategoryId === 'number')

const getTransactionId = (tx?: MasterTransaction): string | undefined => tx?.id

const getTransactionStateId = (tx?: MasterTransaction): number | undefined => {
  const id = tx?.state?.id
  return typeof id === 'number' ? id : undefined
}

const isPendingStateId = (stateId?: number) =>
  typeof stateId === 'number' &&
  (isTransactionStatusPending(stateId) || isTransactionStatusAwaitingApproval(stateId))

export const DepositTransactionPage: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const locale = useSessionLanguage()
  const apiClient = useApiClient(ClientApiClient)

  const { target, id, status, walletId, pspId } = useParams<{
    target: DepositTarget
    id: string
    status: Status
    walletId?: string
    pspId?: string
  }>()
  const { values } = useFormikContext<DepositFormValues>()

  const isPA = values.dashboardContext === 'payment-agent'
  const resolvedTarget: DepositTarget = target || (values?.wallet ? 'wallet' : 'trading-account')
  const resolvedId: string | undefined =
    id || walletId || values?.wallet?.id || values?.tradingAccount?.id

  const onTryAgain = () => {
    const search = location.search
    if (!resolvedId) {
      const fallbackPath = isPA
        ? '/dashboard/payment-agent/deposit'
        : `/dashboard/traders-room/deposit/${resolvedTarget || 'trading-account'}`
      navigate(`${fallbackPath}${search}`, { replace: true })
      return
    }
    navigate(
      `${getDepositBasePath(resolvedTarget, resolvedId, values.dashboardContext, pspId)}${search}`,
      { replace: true }
    )
  }

  const { transaction, providerCategoryId } = useMemo(() => {
    const s = location.state as unknown
    if (isRedirectStorageState(s)) {
      return { transaction: s.data, providerCategoryId: s.providerCategoryId }
    }
    if (isSuccessLocationState(s)) {
      return { transaction: s.data, providerCategoryId: s.providerCategoryId }
    }
    return {
      transaction: undefined as MasterTransaction | undefined,
      providerCategoryId: undefined as number | undefined,
    }
  }, [location.state])

  const txId = useMemo(() => getTransactionId(transaction), [transaction])

  const [currentStateId, setCurrentStateId] = useState<number | undefined>(
    getTransactionStateId(transaction)
  )

  useEffect(() => {
    setCurrentStateId(getTransactionStateId(transaction))
  }, [txId])

  const isCardProvider = useMemo(
    () =>
      typeof providerCategoryId === 'number'
        ? isPaymentProviderCardProviderType(providerCategoryId)
        : false,
    [providerCategoryId]
  )

  const isBankProvider = useMemo(
    () =>
      typeof providerCategoryId === 'number'
        ? isPaymentProviderBankType(providerCategoryId, transaction)
        : false,
    [providerCategoryId, transaction]
  )

  const isPending = useMemo(() => isPendingStateId(currentStateId), [currentStateId])

  const isUnsuccessful = useMemo(
    () => typeof currentStateId === 'number' && isTransactionStatusUnSuccessful(currentStateId),
    [currentStateId]
  )

  useEffect(() => {
    if (!isCardProvider) return
    if (!isPending) return
    if (!txId) return

    let cancelled = false

    const refreshOnce = async () => {
      try {
        const statusHistory = await apiClient.getTransactionStatusHistory(txId, locale)
        if (cancelled) return

        const latest = statusHistory.history.reduce((acc, item) => {
          if (!acc) return item
          return new Date(item.createdDate) > new Date(acc.createdDate) ? item : acc
        }, statusHistory.history[0])

        const latestStateId =
          typeof latest?.transactionState?.id === 'number'
            ? latest.transactionState.id
            : TransactionStatus.None

        setCurrentStateId(latestStateId)

        const basePath = getDepositBasePath(
          resolvedTarget,
          resolvedId!,
          values.dashboardContext,
          pspId
        )

        if (isTransactionStatusUnSuccessful(latestStateId)) {
          if (status !== 'failed') {
            navigate(`${basePath}/transaction/failed`, {
              replace: true,
              state: location.state,
            })
          }
          return
        }

        if (!isPendingStateId(latestStateId)) {
          if (status !== 'succeed') {
            navigate(`${basePath}/transaction/succeed`, {
              replace: true,
              state: location.state,
            })
          }
        }
      } catch {
        // ignore
      }
    }

    void refreshOnce()
    return () => {
      cancelled = true
    }
  }, [
    apiClient,
    isCardProvider,
    isPending,
    locale,
    location.state,
    navigate,
    txId,
    isPA,
    resolvedId,
    resolvedTarget,
    status,
  ])

  const effectiveStatus: Status = useMemo(() => {
    if (status === 'failed') return 'failed'

    if (isUnsuccessful) return 'failed'

    if (isBankProvider) return 'completed'

    if (isCardProvider && isPending) return 'completed'

    return status || 'failed'
  }, [isBankProvider, isCardProvider, isPending, isUnsuccessful, status])

  const onViewHistory = () => {
    if (values.dashboardContext === 'payment-agent') {
      navigate('/dashboard/payment-agent/transaction-history')
      return
    }
    navigate('/dashboard/traders-room/transaction-history')
  }

  const onNewDeposit = () => {
    if (values.dashboardContext === 'payment-agent') {
      navigate('/dashboard/payment-agent/deposit')
      return
    }
    navigate('/dashboard/traders-room/deposit')
  }

  if (effectiveStatus === 'completed') {
    return (
      <DepositPendingContent
        transaction={transaction}
        providerCategoryId={providerCategoryId}
        onViewHistory={onViewHistory}
        onNewDeposit={onNewDeposit}
      />
    )
  }
  if (effectiveStatus === 'failed') {
    const searchParams = new URLSearchParams(location.search)
    const reason = searchParams.get('reason')
    return <DepositFailedContent onTryAgain={onTryAgain} reason={reason} />
  }
  return (
    <DepositSuccessContent
      providerCategoryId={providerCategoryId}
      onViewHistory={onViewHistory}
      onNewDeposit={onNewDeposit}
    />
  )
}
